import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import * as Styles from "@universityofmaryland/web-styles-library";
import * as typography from "@universityofmaryland/web-styles-library/typography";
import { theme } from "@universityofmaryland/web-utilities-library/theme";
import { EmptyState } from "../../states/empty.js";
import { PaginationState } from "../../states/pagination.js";
import { Announcer } from "../../states/announcer.js";
import { dispatch, eventNames } from "../../helpers/events/index.js";
async function setShadowStyles({
  shadowRoot,
  styles
}) {
  const styleElement = document.createElement("style");
  const optimizedCss = await Styles.utilities.transform.css.removeDuplicates(
    styles
  );
  styleElement.textContent = optimizedCss;
  shadowRoot.appendChild(styleElement);
}
function createFallbackMessage(categoryNames, isThemeDark = false) {
  const formattedNames = categoryNames.join(", ");
  const message = `No events found for "${formattedNames}" <span> Other upcoming events:</span>`;
  const messageElement = new ElementBuilder(document.createElement("p")).styled(
    typography.sans.compose("extralarge", {
      theme: theme.fontColor(isThemeDark)
    })
  ).withStyles({
    textAlign: "center",
    margin: "0 auto",
    ["& span"]: {
      display: "block"
    }
  }).withHTML(message);
  const container = new ElementBuilder(document.createElement("div")).withClassName("feed-events-fallback-message").withChild(messageElement).withStyles({
    element: {
      marginBottom: Styles.token.spacing.lg,
      padding: `${Styles.token.spacing.md} ${Styles.token.spacing.lg}`
    }
  }).build();
  return container;
}
function createDisplayHandlers(config) {
  const {
    displayStrategy,
    layoutStrategy,
    helpers,
    cardMappingOptions,
    isLazyLoad,
    numberOfColumnsToShow = 1,
    numberOfRowsToStart,
    noResultsConfig = {},
    lazyLoadCallback
  } = config;
  const displayNoResults = (props) => {
    const container = helpers.getContainer();
    const shadowRoot = helpers.getShadowRoot();
    const { message, linkUrl, linkText, isThemeDark } = {
      ...noResultsConfig,
      ...props
    };
    const emptyState = new EmptyState({
      message,
      linkUrl,
      linkText,
      isThemeDark
    });
    const announcer = new Announcer({ message: message || "No results found" });
    container.innerHTML = "";
    emptyState.render(container);
    container.appendChild(announcer.getElement());
    helpers.setStyles(emptyState.styles);
    dispatch(container, eventNames.FEED_ERROR, {
      error: "No results found",
      message,
      isThemeDark
    });
    setTimeout(() => {
      const styles = helpers.getStyles();
      if (shadowRoot) {
        setShadowStyles({ shadowRoot, styles });
      }
    }, 100);
  };
  const displayResults = async (props) => {
    const { feedData } = props;
    const container = helpers.getContainer();
    const grid = container.querySelector(
      `#${layoutStrategy.getId()}`
    );
    const existingLoader = container.querySelector(".umd-loader-container");
    existingLoader?.remove();
    const existingPagination = container.querySelector(
      `.${Styles.layout.alignment.block.center.className}`
    );
    existingPagination?.remove();
    helpers.setOffset(feedData.length);
    const entries = feedData.map(
      (entry) => displayStrategy.mapEntryToCard(entry, cardMappingOptions)
    );
    entries.forEach((entry) => {
      grid.appendChild(entry.element);
      helpers.setStyles(entry.styles);
    });
    if (isLazyLoad && lazyLoadCallback) {
      const pagination = new PaginationState({
        totalEntries: helpers.getTotalEntries(),
        offset: helpers.getOffset(),
        isLazyLoad: true,
        callback: lazyLoadCallback,
        isThemeDark: cardMappingOptions.isThemeDark
      });
      const paginationElement = pagination.render(container);
      if (paginationElement) {
        helpers.setStyles(paginationElement.styles);
      }
    }
    const shadowRoot = helpers.getShadowRoot();
    if (shadowRoot) {
      await setShadowStyles({
        shadowRoot,
        styles: helpers.getStyles()
      });
    }
  };
  const displayResultStart = async (props) => {
    const { feedData, layoutElement, isFallback, categoryNames } = props;
    const container = helpers.getContainer();
    const totalEntries = helpers.getTotalEntries();
    const showAmount = numberOfColumnsToShow * numberOfRowsToStart;
    const message = isLazyLoad ? `Showing ${showAmount} of ${totalEntries} articles` : `Showing ${showAmount} articles`;
    if (isFallback && categoryNames && categoryNames.length > 0) {
      const fallbackMessage = createFallbackMessage(
        categoryNames,
        cardMappingOptions.isThemeDark || false
      );
      container.appendChild(fallbackMessage.element);
      helpers.setStyles(fallbackMessage.styles);
      const shadowRoot = helpers.getShadowRoot();
      if (shadowRoot) {
        await setShadowStyles({
          shadowRoot,
          styles: helpers.getStyles()
        });
      }
    }
    layoutElement.element.setAttribute("id", layoutStrategy.getId());
    container.appendChild(layoutElement.element);
    helpers.setStyles(layoutElement.styles);
    dispatch(container, eventNames.FEED_LOADED, {
      items: feedData,
      count: feedData.length,
      total: totalEntries || feedData.length,
      isFallback,
      categoryNames
    });
    await displayResults({ feedData });
    const announcer = new Announcer({ message });
    container.appendChild(announcer.getElement());
  };
  return {
    displayNoResults,
    displayResults,
    displayResultStart
  };
}
export {
  createDisplayHandlers,
  setShadowStyles
};
//# sourceMappingURL=displayHandler.js.map
